import type { BannerStateInfo } from './BannerStateContainer.ts';

/**
 * Global floating-banner stack, matching `bannerStore.ts`.
 * Tesseract isolates modules per request, and floating banners have no server-side registration or
 * hydration payload, so both the server render and first client render begin with an empty stack.
 */

type Listener = () => void;
type Mutation = (current: BannerStateInfo[]) => BannerStateInfo[] | undefined;

const NO_FLOATING_BANNERS: BannerStateInfo[] = Object.freeze([]) as unknown as BannerStateInfo[];

let floatingBanners: BannerStateInfo[] = NO_FLOATING_BANNERS;
const listeners = new Set<Listener>();
let pending: Mutation[] = [];
let pendingFlush: Promise<void> | null = null;

export const getFloatingBanners = (): BannerStateInfo[] => floatingBanners;

export const subscribeToFloatingBanners = (listener: Listener): (() => void) => {
	listeners.add(listener);
	return () => listeners.delete(listener);
};

const commit = (next: BannerStateInfo[]): void => {
	floatingBanners = next;
	Array.from(listeners).forEach((listener) => listener());
};

const enqueue = (mutation: Mutation): void => {
	pending.push(mutation);
	if (pendingFlush) {
		return;
	}

	pendingFlush = Promise.resolve().then(() => {
		pendingFlush = null;
		const queued = pending;
		pending = [];
		let next = floatingBanners;

		for (const mutate of queued) {
			next = mutate(next) ?? next;
		}

		if (next !== floatingBanners) {
			commit(next);
		}
	});
};

export const showFloatingBanner = (name: string, height: number): void => {
	enqueue((current) => {
		const index = current.findIndex((banner) => banner.name === name);
		if (index === -1) {
			return [...current, { name, height }];
		}
		if (current[index].height === height) {
			return undefined;
		}
		const next = [...current];
		next[index] = { name, height };
		return next;
	});
};

export const hideFloatingBanner = (name: string): void => {
	enqueue((current) => {
		const index = current.findIndex((banner) => banner.name === name);
		if (index === -1) {
			return undefined;
		}
		const next = [...current];
		next.splice(index, 1);
		return next;
	});
};

export const seedFloatingBanners = (initial: BannerStateInfo[]): void => {
	if (floatingBanners.length === 0 && initial.length > 0) {
		commit([...initial]);
	}
};

export const clearFloatingBannerStoreForTesting = (): void => {
	floatingBanners = NO_FLOATING_BANNERS;
	listeners.clear();
	pending = [];
	pendingFlush = null;
};
